<template>
  <div :class="['robot-entry', { 'robot-entry--empty': !hasCounts }]" @click="$emit('toggle')">
    <div class="robot-entry__icon">
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M16 2.66666C16.5862 2.66769 17.1556 2.86183 17.6204 3.21904C18.0851 3.57624 18.4192 4.07662 18.571 4.64278C18.7228 5.20894 18.6838 5.80934 18.4601 6.35112C18.2364 6.8929 17.8404 7.34588 17.3333 7.63999V7.99999H21.3333C22.7478 7.99999 24.1044 8.56189 25.1046 9.56209C26.1048 10.5623 26.6667 11.9188 26.6667 13.3333V13.4C27.4191 13.5543 28.0952 13.9637 28.5807 14.5588C29.0662 15.154 29.3313 15.8986 29.3313 16.6667C29.3313 17.4347 29.0662 18.1793 28.5807 18.7745C28.0952 19.3697 27.4191 19.779 26.6667 19.9333V21.3333C26.6667 22.7478 26.1048 24.1044 25.1046 25.1046C24.1044 26.1048 22.7478 26.6667 21.3333 26.6667H10.6667C9.25218 26.6667 7.89563 26.1048 6.89543 25.1046C5.89524 24.1044 5.33333 22.7478 5.33333 21.3333V19.9333C4.57981 19.7803 3.90235 19.3715 3.41575 18.7762C2.92915 18.1808 2.66333 17.4356 2.66333 16.6667C2.66333 15.8978 2.92915 15.1525 3.41575 14.5572C3.90235 13.9618 4.57981 13.553 5.33333 13.4V13.3333C5.33333 11.9188 5.89524 10.5623 6.89543 9.56209C7.89563 8.56189 9.25218 7.99999 10.6667 7.99999H14.6667V7.63999C14.1596 7.34588 13.7636 6.8929 13.5399 6.35112C13.3162 5.80934 13.2772 5.20894 13.429 4.64278C13.5808 4.07662 13.9149 3.57624 14.3796 3.21904C14.8444 2.86183 15.4138 2.66769 16 2.66666ZM12 14.6667C11.6464 14.6667 11.3072 14.8071 11.0572 15.0572C10.8071 15.3072 10.6667 15.6464 10.6667 16V18.6667C10.6667 19.0203 10.8071 19.3594 11.0572 19.6095C11.3072 19.8595 11.6464 20 12 20C12.3536 20 12.6928 19.8595 12.9428 19.6095C13.1929 19.3594 13.3333 19.0203 13.3333 18.6667V16C13.3333 15.6464 13.1929 15.3072 12.9428 15.0572C12.6928 14.8071 12.3536 14.6667 12 14.6667ZM20 14.6667C19.6464 14.6667 19.3072 14.8071 19.0572 15.0572C18.8071 15.3072 18.6667 15.6464 18.6667 16V18.6667C18.6667 19.0203 18.8071 19.3594 19.0572 19.6095C19.3072 19.8595 19.6464 20 20 20C20.3536 20 20.6928 19.8595 20.9428 19.6095C21.1929 19.3594 21.3333 19.0203 21.3333 18.6667V16C21.3333 15.6464 21.1929 15.3072 20.9428 15.0572C20.6928 14.8071 20.3536 14.6667 20 14.6667Z" fill="#374957"/>
      </svg>
    </div>
    <div class="robot-entry__bubble-wrap">
      <div class="robot-entry__bubble">
        <template v-if="isLoading">
          <div class="robot-entry__shimmer robot-entry__shimmer--line1" />
          <div class="robot-entry__shimmer robot-entry__shimmer--line2" />
        </template>
        <template v-else-if="hasCounts">
          <p class="robot-entry__greeting">{{ greeting }}</p>
          <p class="robot-entry__counts">{{ countsText }}</p>
        </template>
        <p v-else class="robot-entry__greeting">{{ emptyMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'RobotEntry',
  emits: ['toggle'],
  computed: {
    ...mapState('login', ['userInfo']),
    ...mapState('notifications', {
      automationsTotalCount: state => state.automations.totalCount,
      mentionsUnreadCount: state => state.mentions.unreadCount,
      tasksOpenedCount: state => state.tasks.openedTaskCount,
    }),
    ...mapGetters('notifications', ['badgeCounts']),
    isLoading() {
      return this.automationsTotalCount === null
        || this.mentionsUnreadCount === null
        || this.tasksOpenedCount === null;
    },
    firstName() {
      return this.userInfo?.first_name || this.userInfo?.firstName || 'there';
    },
    greeting() {
      return `Hi ${this.firstName}, checking in:`;
    },
    emptyMessage() {
      return `Nothing to see here, ${this.firstName}. Go enjoy some downtime!`;
    },
    counts() {
      return this.badgeCounts || {};
    },
    hasCounts() {
      const c = this.counts;
      return (c.automations || 0) + (c.mentions || 0) + (c.tasks || 0) + (c.orders || 0) > 0;
    },
    countsText() {
      const c = this.counts;
      const parts = [];
      if (c.automations) parts.push(`{${c.automations}} from automation`);
      if (c.mentions) parts.push(`{${c.mentions}} mentions`);
      if (c.tasks) parts.push(`{${c.tasks}} tasks`);
      if (c.orders) parts.push(`{${c.orders}} orders`);
      return parts.join(', ');
    },
  },
  mounted() {
    this.$store.dispatch('notifications/fetchAutomations');
    this.$store.dispatch('notifications/fetchApprovals');
    this.$store.dispatch('notifications/fetchMentions');
    this.$store.dispatch('notifications/fetchTasks');
    this.$store.dispatch('notifications/fetchOrdersTabsBadge');
  },
};
</script>

<style scoped>
.robot-entry {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  padding: 12px 0;
  cursor: pointer;
  margin-bottom: 12px;
}

.robot-entry__icon {
  flex-shrink: 0;
}

.robot-entry__bubble-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  padding-left: 10px;
}

.robot-entry__bubble-wrap::before {
  content: '';
  position: absolute;
  left: 0;
  top: 10px;
  width: 16px;
  height: 18px;
  background: #FAEFD8;
  clip-path: polygon(100% 0%, 0% 50%, 100% 100%);
}

.robot-entry__bubble {
  background: #FAEFD8;
  border-radius: 20px;
  padding: 8px 16px;
  transition: background 0.2s;
}

.robot-entry:hover .robot-entry__bubble {
  background: #F6E4BC;
}

.robot-entry:hover .robot-entry__bubble-wrap::before {
  background: #F6E4BC;
}

/* Empty state (no notifications) */
.robot-entry--empty .robot-entry__bubble {
  background: #E4F3F5;
}

.robot-entry--empty .robot-entry__bubble-wrap::before {
  background: #E4F3F5;
}

.robot-entry--empty:hover .robot-entry__bubble {
  background: #C5E3E7;
}

.robot-entry--empty:hover .robot-entry__bubble-wrap::before {
  background: #C5E3E7;
}

.robot-entry__greeting {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0 0 4px;
}

.robot-entry__counts {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0 0 2px;
}

.robot-entry__shimmer {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(90deg, rgba(0,0,0,0.06) 25%, rgba(0,0,0,0.02) 50%, rgba(0,0,0,0.06) 75%);
  background-size: 200% 100%;
  animation: robot-shimmer 1.5s ease-in-out infinite;
}

.robot-entry__shimmer--line1 {
  width: 75%;
  margin-bottom: 8px;
}

.robot-entry__shimmer--line2 {
  width: 55%;
}

@keyframes robot-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

</style>
